import { Download, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { renderLengthBeats, renderProject } from '../audio/render';
import type { PlayMode } from '../audio/sequencer';
import { downloadBlob, encodeWav, safeName, type BitDepth } from '../audio/wav';
import { useStore } from '../store/useStore';
import { NumberField, Segmented, Select } from './ui/Controls';

/** Bounce to WAV — full mix or per-track stems. */
export function ExportDialog({ onClose }: { onClose: () => void }) {
  const project = useStore((s) => s.project);
  const notify = useStore((s) => s.notify);
  const [mode, setMode] = useState<PlayMode>(project.scenes.length ? 'song' : 'pattern');
  const [loops, setLoops] = useState(4);
  const [depth, setDepth] = useState<BitDepth>(24);
  const [rate, setRate] = useState(44100);
  const [tail, setTail] = useState(1.5);
  const [stems, setStems] = useState(false);
  const [busy, setBusy] = useState(false);

  const beats = renderLengthBeats(project, { mode, loops, tail });
  const seconds = (beats * 60) / project.bpm + tail;

  const run = async () => {
    setBusy(true);
    try {
      const base = safeName(project.name);
      if (stems) {
        for (const track of project.tracks) {
          if (!track.sourceId || !track.patternId) continue;
          const buf = await renderProject(project, {
            mode,
            loops,
            tail,
            sampleRate: rate,
            onlyTracks: [track.id],
          });
          downloadBlob(encodeWav(buf, depth), `${base}-${safeName(track.name)}.wav`);
        }
        notify('Stems exported');
      } else {
        const buf = await renderProject(project, { mode, loops, tail, sampleRate: rate });
        downloadBlob(encodeWav(buf, depth), `${base}-${mode}.wav`);
        notify('Mixdown exported');
      }
      onClose();
    } catch (err) {
      notify(`Export failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="panel zoom-in w-[26rem] space-y-4 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="display text-sm">Export audio</h2>
          <button className="btn btn-icon" onClick={onClose}>
            <X size={13} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="label mb-1">Range</div>
            <Segmented<PlayMode>
              value={mode}
              onChange={setMode}
              options={[
                { value: 'song', label: 'Full arrangement' },
                { value: 'pattern', label: 'Pattern loop' },
              ]}
            />
          </div>

          {mode === 'pattern' && (
            <div className="flex items-center gap-2">
              <span className="label w-20">Repeats</span>
              <NumberField value={loops} min={1} max={64} width="w-20" onChange={setLoops} />
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="label w-20">Bit depth</span>
            <Select<string>
              value={String(depth)}
              options={[
                { value: '16', label: '16-bit PCM' },
                { value: '24', label: '24-bit PCM' },
                { value: '32', label: '32-bit float' },
              ]}
              onChange={(v) => setDepth(Number(v) as BitDepth)}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="label w-20">Sample rate</span>
            <Select<string>
              value={String(rate)}
              options={[
                { value: '44100', label: '44.1 kHz' },
                { value: '48000', label: '48 kHz' },
                { value: '96000', label: '96 kHz' },
              ]}
              onChange={(v) => setRate(Number(v))}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="label w-20">Tail</span>
            <NumberField value={tail} min={0} max={10} step={0.5} width="w-20" suffix="s" onChange={setTail} />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-ink-200">
            <input
              type="checkbox"
              checked={stems}
              onChange={(e) => setStems(e.target.checked)}
              className="accent-volt-500"
            />
            Export separate stems (one WAV per track)
          </label>
        </div>

        <div className="mono rounded-lg border border-ink-700 bg-ink-850 px-3 py-2 text-[10px] text-ink-400">
          {beats.toFixed(0)} beats · {Math.floor(seconds / 60)}:
          {String(Math.floor(seconds % 60)).padStart(2, '0')} @ {project.bpm} BPM
        </div>

        <button className="btn btn-primary w-full justify-center" disabled={busy} onClick={run}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          {busy ? 'Rendering…' : 'Render and download'}
        </button>
      </div>
    </div>
  );
}
